import { logger } from '@/lib/logging';

export interface StreamRecorderConfig {
    mediaStream: MediaStream;
    interviewId: string;
    apiBaseUrl: string;
    onStatusChange?: (status: 'recording' | 'uploading' | 'completed' | 'error') => void;
    onError?: (error: Error) => void;
}

interface UploadQueueItem {
    blob: Blob;
    partNumber?: number; // Optional - assigned when processing starts
    retries: number;
}

export class StreamRecorder {
    private mediaRecorder: MediaRecorder | null = null;
    private config: StreamRecorderConfig;
    private uploadId: string | null = null;
    private key: string | null = null;
    private partNumber = 1;
    private uploadQueue: UploadQueueItem[] = [];
    private pendingChunk: { blob: Blob; size: number } | null = null;
    private isUploading = false;
    private uploadedParts: { PartNumber: number; ETag: string }[] = [];
    private isRecording = false;
    private fatalError: Error | null = null;
    private static readonly MIN_PART_SIZE = 5 * 1024 * 1024; // 5MB non-final part requirement

    constructor(config: StreamRecorderConfig) {
        this.config = config;
    }

    public async start(): Promise<void> {
        try {
            logger.info('Starting StreamRecorder');

            // 1. Initialize Multipart Upload if not already done
            if (!this.uploadId) {
                await this.initUpload();
            }

            this.startMediaRecorder();

        } catch (error) {
            logger.error('Failed to start StreamRecorder', { error: String(error) });
            this.config.onError?.(error instanceof Error ? error : new Error('Failed to start recording'));
        }
    }

    private startMediaRecorder() {
        // 2. Start MediaRecorder
        this.mediaRecorder = new MediaRecorder(this.config.mediaStream, {
            mimeType: 'video/webm;codecs=vp8,opus'
        });

        this.mediaRecorder.ondataavailable = async (event) => {
            if (event.data && event.data.size > 0) {
                this.queueChunk(event.data);
            }
        };

        this.mediaRecorder.onstop = async () => {
            logger.info('MediaRecorder stopped');
            // We don't complete upload here automatically anymore, 
            // because we might be switching streams.
            // Explicit call to stop() or complete() is needed.
            await this.flushQueue();
        };

        // Slice every 20 seconds to increase average part size (~6MB) and stay above the 5MB non-final requirement.
        this.mediaRecorder.start(20000);
        this.isRecording = true;
        this.config.onStatusChange?.('recording');
    }

    public async switchStream(newStream: MediaStream): Promise<void> {
        logger.info('Switching stream in StreamRecorder');

        // 1. Stop current recorder
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
            // Wait for stop event and flush
            await new Promise<void>(resolve => {
                if (!this.mediaRecorder) return resolve();
                this.mediaRecorder.onstop = async () => {
                    await this.flushQueue();
                    resolve();
                };
            });
        }

        // 2. Update config
        this.config.mediaStream = newStream;

        // 3. Start new recorder
        this.startMediaRecorder();
    }

    /**
     * Stop recording and return upload metadata for server-side completion
     * Returns metadata instead of completing upload to allow background processing
     */
    public async stop(): Promise<{
        uploadId: string;
        key: string;
        parts: Array<{ PartNumber: number; ETag: string }>;
    } | null> {
        logger.info('[STREAM RECORDER] Stop called', {
            interviewId: this.config.interviewId,
            isRecording: this.isRecording,
            mediaRecorderState: this.mediaRecorder?.state,
            queueLength: this.uploadQueue.length,
            uploadedPartsCount: this.uploadedParts.length,
            partNumber: this.partNumber
        });

        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            // Force a final dataavailable before stopping to avoid missing the last chunk
            this.mediaRecorder.requestData();
            this.mediaRecorder.stop();
            this.isRecording = false;
            // Flush any buffered small chunk as the final part (final part may be <5MB)
            this.flushPendingChunkToQueue(true);
            
            // Wait for flush to ensure all chunks are uploaded
            logger.info('[STREAM RECORDER] Waiting for queue flush before returning metadata', {
                interviewId: this.config.interviewId,
                queueLength: this.uploadQueue.length
            });
            await this.flushQueue();

            // OK FIX: Single verification call (removed duplicate - DRY principle)
            // Final verification: ensure parts are committed server-side
            const verified = await this.verifyPartsAvailable();
            if (!verified) {
                logger.error('[STREAM RECORDER] Final verification failed - parts not fully available, not returning metadata', {
                    interviewId: this.config.interviewId,
                    uploadId: this.uploadId,
                    key: this.key,
                    uploadedPartsCount: this.uploadedParts.length
                });
                return null;
            }

            if (this.fatalError) {
                logger.error('[STREAM RECORDER] Recording failed - not returning upload metadata', {
                    interviewId: this.config.interviewId,
                    error: this.fatalError.message,
                    errorName: this.fatalError.name
                });
                return null;
            }
            
            // OK FIX: Ensure at least one part was uploaded (prevents empty parts array)
            // If no chunks were recorded, uploadedParts.length = 0 and partNumber = 1,
            // making expectedParts = 0, which incorrectly passes the length check
            if (this.uploadedParts.length === 0) {
                logger.error('[STREAM RECORDER] No parts uploaded - recording may have been empty or failed', {
                    interviewId: this.config.interviewId,
                    partNumber: this.partNumber,
                    uploadId: this.uploadId,
                    key: this.key
                });
                return null;
            }
            
            const expectedParts = this.partNumber - 1;
            if (this.uploadedParts.length !== expectedParts) {
                logger.error('[STREAM RECORDER] Multipart upload incomplete - missing parts', {
                    interviewId: this.config.interviewId,
                    expectedParts,
                    uploadedParts: this.uploadedParts.length,
                    partNumber: this.partNumber,
                    uploadedPartsDetails: this.uploadedParts.map(p => ({
                        PartNumber: p.PartNumber,
                        hasETag: !!p.ETag,
                        ETagLength: p.ETag?.length || 0
                    }))
                });
                return null;
            }
            
            // Validate all parts have valid ETags
            const invalidParts = this.uploadedParts.filter(p => !p.ETag || typeof p.ETag !== 'string' || p.ETag.trim() === '');
            if (invalidParts.length > 0) {
                logger.error('[STREAM RECORDER] Invalid parts detected - missing or empty ETags', {
                    interviewId: this.config.interviewId,
                    invalidPartsCount: invalidParts.length,
                    invalidParts: invalidParts.map(p => ({ PartNumber: p.PartNumber, hasETag: !!p.ETag })),
                    totalParts: this.uploadedParts.length
                });
                return null;
            }
            
            // Return metadata for server-side completion instead of completing here
            if (!this.uploadId || !this.key) {
                logger.warn('[STREAM RECORDER] No upload metadata available', {
                    interviewId: this.config.interviewId,
                    hasUploadId: !!this.uploadId,
                    hasKey: !!this.key
                });
                return null;
            }

            logger.info('[STREAM RECORDER] Stopped recording, returning upload metadata for server-side completion', {
                interviewId: this.config.interviewId,
                uploadId: this.uploadId,
                key: this.key,
                partsCount: this.uploadedParts.length,
                partsDetails: this.uploadedParts.map(p => ({
                    PartNumber: p.PartNumber,
                    ETag: p.ETag.substring(0, 20) + '...' // Log partial ETag for debugging
                }))
            });

            return {
                uploadId: this.uploadId,
                key: this.key,
                parts: [...this.uploadedParts] // Return copy of parts array
            };
        }
        
        logger.warn('[STREAM RECORDER] Stop called but mediaRecorder is inactive', {
            interviewId: this.config.interviewId,
            mediaRecorderState: this.mediaRecorder?.state,
            isRecording: this.isRecording
        });
        return null;
    }

    /**
     * Verify parts are present on the server (list parts) before returning metadata.
     * Retries a few times to allow eventual consistency.
     */
    private async verifyPartsAvailable(): Promise<boolean> {
        if (!this.uploadId || !this.key) return false;
        const maxAttempts = 3;
        const delayMs = 700;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const response = await fetch(
                    `${this.config.apiBaseUrl}/api/v1/interviews/${this.config.interviewId}/recording/multipart?action=verify`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            uploadId: this.uploadId,
                            key: this.key,
                            expectedParts: this.uploadedParts.length
                        })
                    }
                );
                if (response.ok) {
                    logger.info('[STREAM RECORDER] Verified parts available on server', {
                        interviewId: this.config.interviewId,
                        attempt,
                        uploadId: this.uploadId,
                        key: this.key,
                        expectedParts: this.uploadedParts.length
                    });
                    return true;
                }

                logger.warn('[STREAM RECORDER] Parts verification failed, will retry', {
                    interviewId: this.config.interviewId,
                    attempt,
                    status: response.status,
                    uploadId: this.uploadId,
                    key: this.key
                });
            } catch (err) {
                logger.warn('[STREAM RECORDER] Parts verification error, will retry', {
                    interviewId: this.config.interviewId,
                    attempt,
                    error: String(err)
                });
            }

            await new Promise(res => setTimeout(res, delayMs));
        }

        logger.error('[STREAM RECORDER] Parts verification failed after retries', {
            interviewId: this.config.interviewId,
            uploadId: this.uploadId,
            key: this.key,
            expectedParts: this.uploadedParts.length
        });
        return false;
    }

    /**
     * Complete upload client-side (for backward compatibility if needed)
     * Note: New flow should use server-side completion via Inngest
     */
    public async completeUpload(): Promise<{ key: string; url: string } | null> {
        return await this._completeUpload();
    }

    private async initUpload() {
        const response = await fetch(`${this.config.apiBaseUrl}/api/v1/interviews/${this.config.interviewId}/recording/multipart?action=init`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                filename: `interview-${this.config.interviewId}-${Date.now()}.webm`,
                mimeType: 'video/webm'
            })
        });

        if (!response.ok) throw new Error('Failed to init upload');

        const result = await response.json();
        this.uploadId = result.data.uploadId;
        this.key = result.data.key;
        logger.info('Multipart upload initialized', { uploadId: this.uploadId });
    }

    private queueChunk(blob: Blob) {
        // OK FIX: Don't assign partNumber here - assign when processing starts
        // This prevents race condition where partNumber increments before upload succeeds
        logger.info('[STREAM RECORDER] Chunk queued for upload', {
            interviewId: this.config.interviewId,
            currentPartNumber: this.partNumber,
            chunkSize: blob.size,
            queueLength: this.uploadQueue.length + 1,
            uploadId: this.uploadId
        });
        this.enqueueWithMinimumSize(blob);
        this.processQueue();
    }

    /**
     * Buffer small chunks and combine until we meet the 5MB minimum (except final part).
     */
    private enqueueWithMinimumSize(blob: Blob) {
        const size = blob.size;

        if (this.pendingChunk) {
            const combined = new Blob([this.pendingChunk.blob, blob], { type: blob.type || 'video/webm' });
            const combinedSize = this.pendingChunk.size + size;

            if (combinedSize >= StreamRecorder.MIN_PART_SIZE) {
                this.uploadQueue.push({
                    blob: combined,
                    retries: 0
                });
                this.pendingChunk = null;
            } else {
                this.pendingChunk = { blob: combined, size: combinedSize };
            }
            return;
        }

        if (size < StreamRecorder.MIN_PART_SIZE) {
            this.pendingChunk = { blob, size };
            return;
        }

        this.uploadQueue.push({
            blob,
            retries: 0
        });
    }

    /**
     * Flush buffered chunk to the queue (final chunk is allowed to be <5MB).
     */
    private flushPendingChunkToQueue(isFinal = false) {
        if (!this.pendingChunk) return;
        // OK FIX: Don't assign partNumber here - it will be assigned when processing starts
        if (!isFinal && this.pendingChunk.size < StreamRecorder.MIN_PART_SIZE) {
            return; // safety check
        }
        this.uploadQueue.push({
            blob: this.pendingChunk.blob,
            retries: 0
        });
        logger.info('[STREAM RECORDER] Flushed buffered chunk', {
            interviewId: this.config.interviewId,
            chunkSize: this.pendingChunk.size
        });
        this.pendingChunk = null;
    }

    private async processQueue() {
        if (this.isUploading || this.uploadQueue.length === 0) return;

        this.isUploading = true;
        const item = this.uploadQueue[0];

        try {
            this.config.onStatusChange?.('uploading');

            // OK FIX: Assign partNumber when starting upload, not when queuing
            // This ensures partNumber is sequential and matches uploaded parts
            // If item already has partNumber (from retry), use it; otherwise assign current
            if (!item.partNumber) {
                item.partNumber = this.partNumber;
            }
            const currentPartNumber = item.partNumber;

            logger.info('[STREAM RECORDER] Starting chunk upload', {
                interviewId: this.config.interviewId,
                partNumber: currentPartNumber,
                chunkSize: item.blob.size,
                retryAttempt: item.retries,
                uploadId: this.uploadId,
                key: this.key
            });

            const formData = new FormData();
            formData.append('uploadId', this.uploadId!);
            formData.append('key', this.key!);
            formData.append('partNumber', currentPartNumber.toString());
            formData.append('chunk', item.blob);

            const uploadUrl = `${this.config.apiBaseUrl}/api/v1/interviews/${this.config.interviewId}/recording/multipart?action=chunk`;
            
            let response: Response;
            try {
                response = await fetch(uploadUrl, {
                    method: 'POST',
                    body: formData
                });
            } catch (networkError) {
                // Catch network errors (CORS, connection refused, etc.)
                const networkErr = networkError instanceof Error ? networkError : new Error(String(networkError));
                logger.error('[STREAM RECORDER] Network error during chunk upload', {
                    interviewId: this.config.interviewId,
                    partNumber: item.partNumber,
                    error: networkErr.message,
                    errorName: networkErr.name,
                    errorStack: networkErr.stack,
                    uploadUrl,
                    retryAttempt: item.retries
                });
                throw networkErr;
            }

            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                logger.error('Error [STREAM RECORDER] Chunk upload failed with non-OK response', {
                    interviewId: this.config.interviewId,
                    partNumber: item.partNumber,
                    status: response.status,
                    statusText: response.statusText,
                    errorText,
                    retryAttempt: item.retries
                });
                throw new Error(`Failed to upload chunk ${item.partNumber}: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            
            if (!result.data || !result.data.ETag) {
                logger.error('[STREAM RECORDER] Invalid response: missing ETag', {
                    interviewId: this.config.interviewId,
                    partNumber: item.partNumber,
                    response: result
                });
                throw new Error(`Invalid response: missing ETag for chunk ${item.partNumber}`);
            }

            const etag = result.data.ETag;
            // OK FIX: Increment partNumber only after successful upload (after ETag received)
            // This ensures partNumber matches actual uploaded parts
            // Use the partNumber we assigned at the start of this upload
            this.uploadedParts.push({
                PartNumber: currentPartNumber,
                ETag: etag
            });
            
            // Increment for next part (only after successful upload)
            this.partNumber = currentPartNumber + 1;

            logger.info('[STREAM RECORDER] Chunk uploaded successfully', {
                interviewId: this.config.interviewId,
                partNumber: currentPartNumber,
                ETag: etag,
                chunkSize: item.blob.size,
                totalUploadedParts: this.uploadedParts.length
            });

            // Remove from queue
            this.uploadQueue.shift();
            this.config.onStatusChange?.('recording'); // Back to recording status

        } catch (error) {
            const err = error instanceof Error ? error : new Error(String(error));
            const currentPartNumber = item.partNumber || this.partNumber;
            logger.error(`[STREAM RECORDER] Error uploading chunk ${currentPartNumber}`, {
                interviewId: this.config.interviewId,
                partNumber: currentPartNumber,
                error: err.message,
                errorName: err.name,
                errorStack: err.stack,
                retryAttempt: item.retries,
                willRetry: item.retries < 3
            });
            
            item.retries++;
            if (item.retries > 3) {
                logger.error(`[STREAM RECORDER] Recording failed after 3 retries on chunk ${currentPartNumber}`, {
                    interviewId: this.config.interviewId,
                    partNumber: currentPartNumber,
                    totalRetries: item.retries,
                    uploadedPartsCount: this.uploadedParts.length,
                    queueLength: this.uploadQueue.length
                });
                this.fatalError = new Error(`Failed to upload chunk ${currentPartNumber} after ${item.retries} retries`);
                this.uploadQueue = [];
                this.config.onError?.(this.fatalError);
            } else {
                // Exponential backoff: 1s, 2s, 4s
                const backoffMs = 1000 * Math.pow(2, item.retries - 1);
                logger.info(`[STREAM RECORDER] Retrying chunk upload after ${backoffMs}ms`, {
                    interviewId: this.config.interviewId,
                    partNumber: currentPartNumber,
                    retryAttempt: item.retries,
                    backoffMs
                });
                await new Promise(r => setTimeout(r, backoffMs));
            }
        } finally {
            this.isUploading = false;
            // Process next
            if (this.uploadQueue.length > 0) {
                this.processQueue();
            }
        }
    }

    private async flushQueue(): Promise<void> {
        const initialQueueLength = this.uploadQueue.length;
        const startTime = Date.now();
        const maxFlushTime = 60000; // 60 seconds max flush time to prevent hanging
        
        logger.info('[STREAM RECORDER] Flushing upload queue', {
            interviewId: this.config.interviewId,
            queueLength: initialQueueLength,
            uploadedPartsCount: this.uploadedParts.length,
            maxFlushTimeMs: maxFlushTime
        });

        let attempts = 0;
        const maxAttempts = 1000; // Increased to allow more retries
        
        while (this.uploadQueue.length > 0 && attempts < maxAttempts) {
            // Check timeout to prevent hanging indefinitely
            if (Date.now() - startTime > maxFlushTime) {
                logger.error('[STREAM RECORDER] Queue flush timeout exceeded', {
                    interviewId: this.config.interviewId,
                    remainingItems: this.uploadQueue.length,
                    attempts,
                    elapsedTimeMs: Date.now() - startTime,
                    uploadedPartsCount: this.uploadedParts.length
                });
                throw new Error(`Queue flush timeout: ${this.uploadQueue.length} items remaining after ${maxFlushTime}ms`);
            }
            
            await this.processQueue();
            attempts++;
            
            // Small delay to prevent tight loop if processQueue returns immediately
            if (this.uploadQueue.length > 0) {
                await new Promise(r => setTimeout(r, 100));
            }
        }

        if (this.uploadQueue.length > 0) {
            const error = new Error(`Queue flush incomplete: ${this.uploadQueue.length} items remaining after ${attempts} attempts`);
            logger.error('[STREAM RECORDER] Queue flush incomplete - items remaining', {
                interviewId: this.config.interviewId,
                remainingItems: this.uploadQueue.length,
                attempts,
                elapsedTimeMs: Date.now() - startTime,
                uploadedPartsCount: this.uploadedParts.length
            });
            throw error;
        } else {
            logger.info('[STREAM RECORDER] Queue flushed successfully', {
                interviewId: this.config.interviewId,
                itemsProcessed: initialQueueLength,
                uploadedPartsCount: this.uploadedParts.length,
                elapsedTimeMs: Date.now() - startTime,
                attempts
            });
        }
    }

    private async _completeUpload(): Promise<{ key: string; url: string } | null> {
        if (!this.uploadId || !this.key) return null;

        try {
            const response = await fetch(`${this.config.apiBaseUrl}/api/v1/interviews/${this.config.interviewId}/recording/multipart?action=complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uploadId: this.uploadId,
                    key: this.key,
                    parts: this.uploadedParts
                })
            });

            if (!response.ok) throw new Error('Failed to complete upload');

            const result = await response.json();
            logger.info('Multipart upload completed successfully');
            this.config.onStatusChange?.('completed');

            return {
                key: result.data.key,
                url: result.data.url
            };

        } catch (error) {
            logger.error('Failed to complete upload', { error: String(error) });
            this.config.onError?.(error instanceof Error ? error : new Error('Failed to complete upload'));
            return null;
        }
    }
    public get isRecordingActive(): boolean {
        return this.isRecording;
    }

    public async cancel(): Promise<void> {
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.stop();
            this.isRecording = false;
        }
        // Call abort endpoint
        if (this.uploadId && this.key) {
            try {
                await fetch(`${this.config.apiBaseUrl}/api/v1/interviews/${this.config.interviewId}/recording/multipart?action=abort`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        uploadId: this.uploadId,
                        key: this.key
                    })
                });
            } catch (e) {
                logger.error('Failed to abort upload', { error: String(e) });
            }
        }
    }
}
